<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import ConversationAIRecommendations from 'dashboard/routes/dashboard/conversation/ConversationAIRecommendations.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const showGlovelyActions = ref(false);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const currentChatMessages = computed(() => currentChat.value?.messages || []);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const contactProfileRoute = computed(() => ({
  name: 'contacts_edit',
  params: {
    accountId: accountId.value,
    contactId: currentContact.value.id,
  },
}));

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);
const glovelyActionsCount = 2;

const toggleGlovelyActions = () => {
  showGlovelyActions.value = !showGlovelyActions.value;
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex items-center justify-between flex-1 w-full min-w-0 gap-2 px-3 pt-3 pb-2 h-16 xl:h-12"
  >
    <div class="flex items-center justify-start flex-1 max-w-full min-w-0">
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        icon-only
        icon-class="text-2xl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        class="hidden xl:flex"
        hide-offline-status
        rounded-full
      />
      <div
        class="flex flex-col items-start min-w-0 xl:ml-2 overflow-hidden rtl:xl:ml-0 rtl:xl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <router-link
            v-if="currentContact.id"
            v-tooltip="$t('CONTACT_PANEL.VIEW_PROFILE')"
            :to="contactProfileRoute"
            class="flex items-center min-w-0 gap-1 rounded-md text-n-slate-12 hover:text-n-slate-12 hover:bg-n-alpha-2"
            :aria-label="$t('CONTACT_PANEL.VIEW_PROFILE')"
          >
            <span class="text-base font-medium truncate leading-tight">
              {{ currentContact.name }}
            </span>
            <i class="i-lucide-chevron-right text-base flex-shrink-0" />
          </router-link>
          <span
            v-else
            class="text-base font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
        >
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-end flex-shrink-0 gap-2 header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <div class="relative">
        <button
          type="button"
          class="relative inline-flex size-8 items-center justify-center rounded-full border border-n-violet-6 bg-n-violet-2 p-0 text-xs font-semibold text-n-violet-12 transition hover:bg-n-violet-3 sm:w-auto sm:gap-1.5 sm:px-3"
          :aria-label="$t('AI_RECOMMENDATIONS.TITLE')"
          @click="toggleGlovelyActions"
        >
          <span class="i-lucide-sparkles size-3.5" />
          <span class="hidden sm:inline">
            {{ $t('AI_RECOMMENDATIONS.TITLE') }}
          </span>
          <span
            class="absolute -right-1 -top-1 flex aspect-square min-h-4 min-w-4 items-center justify-center rounded-full bg-n-violet-9 text-[10px] font-bold leading-none text-white ring-2 ring-n-surface-1"
          >
            {{ glovelyActionsCount }}
          </span>
        </button>
        <div
          v-if="showGlovelyActions"
          class="fixed inset-x-3 top-16 z-50 sm:absolute sm:inset-x-auto sm:top-full sm:mt-2 sm:w-[22rem] sm:max-w-[calc(100vw-2rem)] sm:ltr:right-0 sm:rtl:left-0"
        >
          <ConversationAIRecommendations
            compact
            :conversation-id="currentChat.id"
            :contact="currentContact"
            :messages="currentChatMessages"
            class="shadow-xl"
          />
        </div>
      </div>
      <ResolveAction
        :conversation-id="currentChat.id"
        :status="currentChat.status"
      />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
